import React from "react";
import PropTypes from "prop-types";
import { Column, Field, Control, Label, Input, Box } from "rbx";
import { useQuery } from "react-apollo";
import { formatCurrency, unmaskCurrency } from "../../utils";
import { SINGLE_SYSTEM_CODE_QUERY } from "../../graphql/SystemCodes/SystemCodes.graphql";
import {
  ItemSelect,
  DateInput,
  CompanyLocationSelect,
  InventoryStorageDimSelect,
  VendorSelect,
  SystemCodeSelect
} from "../inputs";
import { useAuthorization } from "../Authorization";

const PO_CODE_ID = "1";
// const WO_CODE_ID = "2";
const ADJ_CODE_ID = "3";

const ItemTransactionForm = ({
  inputs,
  onChange,
  editing,
  PrimaryStorageDimension,
  SecondaryStorageDimension,
  TertiaryStorageDimension,
  UsePrimaryStorageDimension,
  UseSecondaryStorageDimension,
  UseTertiaryStorageDimension,
  handleQuantityBlur,
  quantityLoading
}) => {
  const Auth = useAuthorization(["ADMIN", "FLEET", "MGMNT", "FIN"]);
  const { data } = useQuery(SINGLE_SYSTEM_CODE_QUERY, {
    variables: {
      where: {
        RecId: inputs.TransTypeRecIdRef
          ? parseInt(inputs.TransTypeRecIdRef)
          : null
      }
    },
    fetchPolicy: "network-only"
  });
  return (
    <div style={{ marginTop: "2rem" }}>
      <Column.Group>
        <Column>
          <Field>
            <Column.Group>
              <Column>
                <Control expanded>
                  <SystemCodeSelect
                    required
                    label="Transaction Type"
                    name="TransTypeRecIdRef"
                    readOnly={editing || quantityLoading || !inputs.CanEditType}
                    value={inputs.TransTypeRecIdRef}
                    where={{ CodeId_not: "2", CodeName: "InvMovementType" }}
                    onChange={onChange}
                  />
                </Control>
              </Column>
              <Column>
                <Control>
                  <ItemSelect
                    orderByNumber
                    required
                    showHelp
                    disabled={editing || quantityLoading}
                    kind="ItemTransaction"
                    label="Item Number"
                    name="ItemNumberRecIdRef"
                    value={inputs.ItemNumberRecIdRef}
                    onChange={onChange}
                  />
                </Control>
              </Column>
              <Column>
                <Control>
                  <ItemSelect
                    required
                    disabled={editing || quantityLoading}
                    kind="ItemTransaction"
                    label="Item Description"
                    name="ItemDescriptionRecIdRef"
                    value={inputs.ItemDescriptionRecIdRef}
                    onChange={onChange}
                  />
                </Control>
              </Column>
              <Column>
                <Control>
                  <Label>Transaction Date</Label>
                  <DateInput
                    disabled={editing || quantityLoading}
                    name="TransDt"
                    value={
                      inputs.TransDt
                        ? new Date(inputs.TransDt).toISOString()
                        : ""
                    }
                    onChange={onChange}
                  />
                </Control>
              </Column>
              <Column>
                <Field>
                  <Control>
                    <Label>Transaction Note</Label>
                    <Input
                      autoComplete="off"
                      name="TransNote"
                      readOnly={editing || quantityLoading}
                      type="text"
                      value={inputs.TransNote}
                      onChange={onChange}
                    />
                  </Control>
                </Field>
              </Column>
            </Column.Group>
          </Field>
          <Field>
            <Column.Group>
              <Column>
                <Control>
                  <CompanyLocationSelect
                    required
                    icon="globe"
                    label="Location"
                    name="LocRecIdRef"
                    readOnly={editing || quantityLoading}
                    value={inputs.LocRecIdRef}
                    variables={{ Active: true }}
                    onChange={onChange}
                  />
                </Control>
              </Column>
              {inputs.LocRecIdRef &&
                PrimaryStorageDimension &&
                UsePrimaryStorageDimension && (
                  <Column>
                    <InventoryStorageDimSelect
                      CompanyLocRecIdRef={inputs.LocRecIdRef}
                      InvStorageDimRecIdRef={PrimaryStorageDimension.RecId}
                      disabled={editing || quantityLoading}
                      icon="globe"
                      label={`${PrimaryStorageDimension.CodeDescription} OH`}
                      name="InvDim1RecIdRef"
                      transactionType={inputs.TransTypeRecIdRef}
                      value={inputs.InvDim1RecIdRef}
                      variables={{ Active: true }}
                      onChange={onChange}
                    />
                  </Column>
                )}
              {inputs.LocRecIdRef &&
                SecondaryStorageDimension &&
                UseSecondaryStorageDimension && (
                  <Column>
                    <InventoryStorageDimSelect
                      CompanyLocRecIdRef={inputs.LocRecIdRef}
                      InvStorageDimRecIdRef={SecondaryStorageDimension.RecId}
                      disabled={editing || quantityLoading}
                      icon="globe"
                      label={`${SecondaryStorageDimension.CodeDescription} OH`}
                      name="InvDim2RecIdRef"
                      transactionType={inputs.TransTypeRecIdRef}
                      value={inputs.InvDim2RecIdRef}
                      variables={{ Active: true }}
                      onChange={onChange}
                    />
                  </Column>
                )}
              {inputs.LocRecIdRef &&
                TertiaryStorageDimension &&
                UseTertiaryStorageDimension && (
                  <Column>
                    <InventoryStorageDimSelect
                      CompanyLocRecIdRef={inputs.LocRecIdRef}
                      InvStorageDimRecIdRef={TertiaryStorageDimension.RecId}
                      disabled={editing || quantityLoading}
                      icon="globe"
                      label={`${TertiaryStorageDimension.CodeDescription} OH`}
                      name="InvDim3RecIdRef"
                      value={inputs.InvDim3RecIdRef}
                      variables={{ Active: true }}
                      onChange={onChange}
                    />
                  </Column>
                )}
              {data &&
                data.SystemCode &&
                data.SystemCode.CodeId !== PO_CODE_ID && (
                  <Column>
                    <Label>Total OH</Label>
                    <Control loading={quantityLoading}>
                      <Input
                        readOnly
                        autoComplete="new"
                        disabled={quantityLoading || quantityLoading}
                        name="OH"
                        type="number"
                        value={inputs.OH}
                      />
                    </Control>
                  </Column>
                )}
              {data &&
                data.SystemCode &&
                data.SystemCode.CodeId !== PO_CODE_ID && (
                  <Column>
                    <Label>Dim OH</Label>
                    <Control loading={quantityLoading}>
                      <Input
                        readOnly
                        autoComplete="new"
                        disabled={quantityLoading}
                        name="DimOHData"
                        type="number"
                        value={inputs.DimOHData}
                      />
                    </Control>
                  </Column>
                )}

              <Column>
                <Control>
                  <Label>Quantity</Label>
                  <Input
                    required
                    autoComplete="new"
                    color={
                      parseFloat(inputs.DimOHData) +
                        parseFloat(inputs.Quantity) <
                        0 &&
                      data &&
                      data.SystemCode &&
                      data.SystemCode.CodeId === ADJ_CODE_ID
                        ? "danger"
                        : ""
                    }
                    name="Quantity"
                    readOnly={editing || quantityLoading}
                    style={{ textAlign: "right" }}
                    type="number"
                    value={inputs.Quantity}
                    onBlur={handleQuantityBlur}
                    onChange={onChange}
                  />
                </Control>
              </Column>
              {Auth && (
                <React.Fragment>
                  <Column>
                    <Control>
                      <Label>{`${
                        data &&
                        data.SystemCode &&
                        data.SystemCode.CodeId === PO_CODE_ID
                          ? "Purchase Cost"
                          : "Cost"
                      }`}</Label>
                      <Input
                        required
                        autoComplete="new"
                        name="Cost"
                        readOnly={
                          editing ||
                          quantityLoading ||
                          (data &&
                            data.SystemCode &&
                            data.SystemCode.CodeId === ADJ_CODE_ID)
                        }
                        type="text"
                        value={inputs.Cost}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                  <Column>
                    <Control>
                      <Label>Extension</Label>
                      <Input
                        readOnly
                        autoComplete="new"
                        name="Extension"
                        type="text"
                        value={
                          inputs.Cost
                            ? formatCurrency(
                                unmaskCurrency(inputs.Cost) * inputs.Quantity
                              )
                            : inputs.Quantity
                        }
                      />
                    </Control>
                  </Column>
                </React.Fragment>
              )}
            </Column.Group>
          </Field>
          {data && data.SystemCode && data.SystemCode.CodeId === PO_CODE_ID && (
            <Box>
              <Field>
                <Column.Group>
                  <Column>
                    <Control>
                      <VendorSelect
                        orderByNumber
                        required
                        disabled={editing || quantityLoading}
                        label="Vendor Number"
                        name="VendorRecIdRef"
                        value={inputs.VendorRecIdRef}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                  <Column>
                    <Control>
                      <VendorSelect
                        required
                        disabled={editing || quantityLoading}
                        label="Vendor Name"
                        name="VendorNameRecIdRef"
                        value={inputs.VendorNameRecIdRef}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                  <Column>
                    <Control>
                      <Label>Purchase Order number</Label>
                      <Input
                        autoComplete="new"
                        name="PONo"
                        readOnly={editing || quantityLoading}
                        type="number"
                        value={inputs.PONo}
                        onChange={onChange}
                      />
                    </Control>
                  </Column>
                </Column.Group>
              </Field>
            </Box>
          )}
        </Column>
      </Column.Group>
    </div>
  );
};
ItemTransactionForm.propTypes = {
  inputs: PropTypes.object,
  onChange: PropTypes.func,
  editing: PropTypes.bool,
  PrimaryStorageDimension: PropTypes.object,
  SecondaryStorageDimension: PropTypes.object,
  TertiaryStorageDimension: PropTypes.object,
  UsePrimaryStorageDimension: PropTypes.bool,
  UseSecondaryStorageDimension: PropTypes.bool,
  UseTertiaryStorageDimension: PropTypes.bool,
  handleQuantityBlur: PropTypes.func,
  quantityLoading: PropTypes.bool
};

ItemTransactionForm.defaultProps = {
  inputs: {},
  PrimaryStorageDimension: null,
  SecondaryStorageDimension: null,
  TertiaryStorageDimension: null
};
export default ItemTransactionForm;
